import { process, State } from '@progress/kendo-data-query';
import {
  GridColumnResizeEvent,
  GridDataStateChangeEvent,
  GridDetailRowProps,
  GridPagerSettings,
  GridRowProps,
} from '@progress/kendo-react-grid';
import { cloneElement, useCallback, useMemo, useState } from 'react';
import {
  varianceGLCodeColumns,
  varianceGLCodeSubColumns,
  varianceGLPeriodColumns,
} from 'src/assets/static/variance_details/VarianceGLPeriodColumns';

import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import Accordion from 'src/components/design-system/Accordion';
import GridWrapper from 'src/components/GridWrapper';
import { handleColumnResize } from 'src/hooks/useScrollDown';
import useVarianceDetails from 'src/hooks/useVarianceDetails';
import { IGLCodeVariance, IGLCodeVarianceLineItem } from 'src/types/VarianceDetails';
import VarianceDetailsToolBar, { DropDownOption, varianceOptions } from './VarianceDetailsToolBar';

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const initialDataState: State = {
  skip: 0,
  take: 20,
};

const defaultProps = {
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  pageable: gridPagerSettings,
  showColumnMenu: true,
  tableTextId: 'Variance GL Periods',
};

const VarianceGlPeriod = () => {
  const { variances, varianceId, varianceGLPeriods, setVarianceGLPeriods, isVarianceLoading } =
    useVarianceDetails();
  const [firstSubordinateColumn, setFirstSubordinateColumn] = useState(varianceGLCodeColumns);
  const [secondSubordinateColumn, setSecondSubordinateColumn] = useState(varianceGLCodeSubColumns);

  const [dataState, setDataState] = useState(initialDataState);
  const { data, total } = useMemo(
    () => process(varianceGLPeriods || [], dataState),
    [dataState, varianceGLPeriods]
  );
  const [varianceDropDownValue, setVarianceDropDownValue] = useState<DropDownOption>(
    varianceOptions[0]
  );

  const clearFilters = useCallback(() => {
    setDataState(initialDataState);
    setVarianceDropDownValue(varianceOptions[0]);
  }, []);

  const handleDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => setDataState(e.dataState),
    []
  );

  const showVariance = useCallback(() => {
    const varianceGLPeriods = variances.acctPeriodVariances.filter(
      (lineItem) => lineItem.hasQtyVariance || lineItem.hasSubTotalVariance
    );
    setVarianceGLPeriods(varianceGLPeriods);
  }, [setVarianceGLPeriods, variances.acctPeriodVariances]);

  const onVarianceDropDownChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const { key } = e.value;
      if (key === 1) {
        setVarianceGLPeriods([...variances?.acctPeriodVariances]);
      } else {
        showVariance();
      }
      setVarianceDropDownValue(e.value);
    },
    [setVarianceGLPeriods, showVariance, variances?.acctPeriodVariances]
  );

  const SecondDetailComponent = useCallback(
    (props: GridDetailRowProps) => {
      const secondChildItems = props.dataItem.itemVariances as IGLCodeVarianceLineItem[];
      return (
        <GridWrapper
          dataSource={secondChildItems}
          columns={secondSubordinateColumn}
          DataItemKey="rowId"
          className="hide-scroll-bar"
          isCustomExpandableRow={true}
          disableAutoResize
          noRecordText="No expenses assigned to the Accounting Period for this GL Code."
        />
      );
    },
    [secondSubordinateColumn]
  );

  const DetailComponent = useCallback(
    (props: GridDetailRowProps) => {
      const firstChildItems = props.dataItem.glCodeVariances as IGLCodeVariance[];
      return (
        <GridWrapper
          dataSource={firstChildItems}
          DataItemKey="rowId"
          columns={firstSubordinateColumn}
          hideScrollBar
          detail={SecondDetailComponent}
          className="hideParentTableHeader hide-scroll-bar"
          isCustomExpandableRow={true}
          disableAutoResize
        />
      );
    },
    [SecondDetailComponent, firstSubordinateColumn]
  );

  const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
    const style = { backgroundColor: '#ddd' };
    let trProps: any = {};
    if (props?.isAltRow) {
      trProps.style = style;
    }
    return cloneElement(trElement, { ...trProps });
  };

  const gridToolbarChildren = useMemo(
    () => (
      <VarianceDetailsToolBar
        clearFilter={clearFilters}
        showFilter
        state={dataState}
        onVarianceDropDownChange={onVarianceDropDownChange}
        varianceDropDownValue={varianceDropDownValue}
      />
    ),
    [clearFilters, dataState, onVarianceDropDownChange, varianceDropDownValue]
  );

  const onColumnResize = useCallback((event: GridColumnResizeEvent) => {
    const { columns, index, end } = event;
    handleColumnResize(event);
    if (end) {
      if (index === 0) return;
      const width = Number(columns[index].width);
      if (index === 1) {
        setFirstSubordinateColumn((prev) => {
          const newWidth = width - 42;
          const cols = [...prev];
          cols[index].width = newWidth;
          return cols;
        });
        setSecondSubordinateColumn((prev) => {
          const newWidth = width - 42;
          const cols = [...prev];
          cols[index - 1].width = newWidth;
          return cols;
        });
      } else {
        setFirstSubordinateColumn((prev) => {
          const cols = [...prev];
          cols[index].width = width;
          return cols;
        });
        setSecondSubordinateColumn((prev) => {
          const cols = [...prev];
          cols[index - 1].width = width;
          return cols;
        });
      }
    }
  }, []);

  return (
    <div className="mb-4">
      <Accordion title={`GL Periods for Variance ${varianceId}`}>
        <GridWrapper
          id="VarianceGLPeriodGrid"
          rowRender={rowRender}
          columns={varianceGLPeriodColumns}
          dataSource={data}
          dataState={dataState}
          total={total}
          gridToolbarChildren={gridToolbarChildren}
          onDataStateChange={handleDataStateChange}
          detail={DetailComponent}
          loading={isVarianceLoading}
          isCustomExpandableRow={true}
          disableAutoResize
          className={'max-h-[400px] mb-5'}
          {...defaultProps}
          onColumnResize={onColumnResize}
        />
      </Accordion>
    </div>
  );
};

export default VarianceGlPeriod;
